import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { STORAGE_KEY, VERSION, MAX_ENTRIES, MAX_JSON_BYTES, emptyState, normalizeProject, normalizeState, validateEntryInput } from './founderData.mjs';

const FounderContext = createContext(null);
const isoNow = () => new Date().toISOString();
const makeId = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `entry-${Date.now()}-${Math.random().toString(36).slice(2)}`);

function readLocal() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { state: emptyState(), warning: '' };
    if (raw.length > MAX_JSON_BYTES) return { state: emptyState(), warning: 'Saved data is unexpectedly large. It was not overwritten. Export the original from browser storage before resetting.' };
    return { state: normalizeState(JSON.parse(raw)), warning: '' };
  } catch (_) {
    return { state: emptyState(), warning: 'Existing local data could not be loaded. Nothing has been overwritten. Import a valid backup or reset explicitly.' };
  }
}

export function FounderProvider({ children }) {
  const [{ state, warning }, setSnapshot] = useState(readLocal);
  const [storageWarning, setStorageWarning] = useState(warning);
  const [blocked, setBlocked] = useState(Boolean(warning));

  useEffect(() => {
    if (blocked) return;
    try {
      const value = JSON.stringify(state);
      if (value.length > MAX_JSON_BYTES) throw new Error('Browser demo capacity reached.');
      window.localStorage.setItem(STORAGE_KEY, value);
      setStorageWarning('');
    } catch (_) {
      setStorageWarning('Browser storage failed or is full. Recent changes may disappear after refresh. Export a JSON backup now.');
    }
  }, [state, blocked]);

  useEffect(() => {
    const onStorage = event => {
      if (event.key !== STORAGE_KEY) return;
      const newest = readLocal();
      setSnapshot(newest);
      setStorageWarning(newest.warning);
      setBlocked(Boolean(newest.warning));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const change = useCallback(updater => {
    if (blocked) throw new Error('Saved local data needs restoration or reset before editing.');
    setSnapshot(previous => ({ ...previous, state: updater(previous.state) }));
  }, [blocked]);

  const createProject = useCallback(raw => {
    change(d => {
      if (d.project) throw new Error('This local workspace already contains a project.');
      return { ...emptyState(), project: normalizeProject({ ...raw, startedAt: isoNow() }) };
    });
  }, [change]);
  const updateProject = useCallback(updates => {
    change(d => ({ ...d, project: d.project ? normalizeProject({ ...d.project, ...updates, startedAt: d.project.startedAt }) : null }));
  }, [change]);
  const toggleTask = useCallback(id => {
    change(d => ({ ...d, tasks: d.tasks.map(t => t.id === id ? { ...t, done: !t.done, completedAt: !t.done ? isoNow() : null } : t) }));
  }, [change]);
  const addEntry = useCallback(raw => {
    const entry = validateEntryInput(raw);
    change(d => {
      if (d.journal.length >= MAX_ENTRIES) throw new Error('Journal capacity reached. Export a backup before adding more.');
      return { ...d, journal: [{ ...entry, id: makeId(), createdAt: isoNow(), updatedAt: null, verification: 'self-reported' }, ...d.journal] };
    });
  }, [change]);
  const updateEntry = useCallback((id, raw) => {
    const entry = validateEntryInput(raw);
    change(d => ({ ...d, journal: d.journal.map(item => item.id === id ? { ...item, ...entry, updatedAt: isoNow(), verification: 'self-reported' } : item) }));
  }, [change]);
  const deleteEntry = useCallback(id => {
    change(d => ({ ...d, journal: d.journal.filter(x => x.id !== id) }));
  }, [change]);
  const restoreDemo = useCallback(imported => {
    const restored = normalizeState(imported);
    setBlocked(false);
    setStorageWarning('');
    setSnapshot({ state: restored, warning: '' });
  }, []);
  const resetDemo = useCallback(() => {
    try { window.localStorage.removeItem(STORAGE_KEY); } catch (_) { /* browser may block storage */ }
    setBlocked(false);
    setStorageWarning('');
    setSnapshot({ state: emptyState(), warning: '' });
  }, []);

  return <FounderContext.Provider value={{ ...state, storageWarning, storageBlocked: blocked, createProject, updateProject, toggleTask, addEntry, updateEntry, deleteEntry, restoreDemo, resetDemo }}>
    {children}
  </FounderContext.Provider>;
}

export function useFounder() {
  const context = useContext(FounderContext);
  if (!context) throw new Error('FounderProvider is missing');
  return context;
}
